// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import ObjectUtils from '../../util/ObjectUtils';
import ComponentHandler from '../../loaders/handlers/ComponentHandler';
import AnimationComponent from '../../animationpack/components/AnimationComponent';

/**
 * For handling loading of animation components
 * @hidden
 */
class AnimationComponentHandler extends ComponentHandler {

  /**
   * @param {...*} args {@link ComponentHandler} constructor arguments.
   */
  constructor(...args) {
    super(...args);
    this._type = 'AnimationComponent';
  }

  /**
   * Create animation component.
   * @returns {AnimationComponent} the created component object
   * @private
   */
  _create() {
    return new AnimationComponent();
  }

  /**
   * Update engine animation component object based on the config.
   * @param {Entity} entity The entity on which this component should be added.
   * @param {Object} config
   * @param {Object} options
   * @returns {Promise} promise that resolves with the component when loading is done.
   */
  update(entity, config, options) {
    const that = this;

    function attachPose(ref, component) {
      return that.loadObject(ref, options).then((pose) => {
        // Cloning the pose since the pose returned by _load is a shared pose and we want to have a separate instance for each entity.
        component._skeletonPose = pose.clone();

        //TODO: This should be move to AnimationSystem
        // Setting the cloned pose to all child entities with meshDataComponent with the same pose id.
        entity.traverse((childEntity) =>{
          const curChildPoseId = ObjectUtils.get(childEntity, ['meshDataComponent', 'currentPose', 'id'], null);
          if (curChildPoseId === pose.id) {
            childEntity.meshDataComponent.currentPose = component._skeletonPose;
          }
        });

      });
    }

    function attachLayers(ref, component) {
      return that.loadObject(ref, options).then((layers) => {
        component.layers = layers;
        component._layersId = ref;
        // link the skeleton to every clipInstance in every state in every layer
        const skeleton = component._skeletonPose && component._skeletonPose._skeleton;
        if (skeleton) {
          for (const layer of layers) {
            for (const stateId of Object.keys(layer._steadyStates)) {
              const state = layer.getStateById(stateId);
              const clipInstance = state && state._sourceTree && state._sourceTree._clipInstance;
              if (clipInstance) {
                clipInstance.setSkeleton(skeleton);
              } else {
                console.log(`[Sumerian] warning: Could not bind clip to skeleton. No clip or clipInstance found for animation state ${state._name}.`)
              }
            }
          }
        }
      });
    }

    return ComponentHandler.prototype.update.call(this, entity, config, options).then((component) => {
      if (!component) { return; }

      const promises = [];

      if (config.poseRef) {
        promises.push(attachPose(config.poseRef, component));
      }

      if (config.layersRef) {
        promises.push(attachLayers(config.layersRef, component));
      }

      return Promise.all(promises).then(() => component);
    });
  }
}

ComponentHandler._registerClass('animation', AnimationComponentHandler);

export default AnimationComponentHandler;
